import { useEffect, useRef, useState } from 'react'

// Matches the JD attachment preview (institute-react AutoJDFill): pages are
// rasterised to images rather than handed to the browser's built-in PDF plugin,
// so the resume renders identically everywhere and carries no viewer chrome.
const RENDER_SCALE = 1.5

// pdfjs is ~2 MB. Importing it lazily keeps it in its own chunk, fetched the
// first time someone opens a resume rather than on every page load.
const loadPdfjs = () => import('pdfjs-dist/webpack.mjs')

/**
 * Rasterise a PDF into page images.
 *
 * @param {string|Blob|null} source URL or Blob to render; null renders nothing.
 * @returns {{pages: string[], isRendering: boolean, error: string|null}}
 */
export default function usePdfPages(source) {
  const [pages, setPages] = useState([])
  const [isRendering, setIsRendering] = useState(false)
  const [error, setError] = useState(null)
  // Guards against a slow render resolving after the drawer moved to another
  // candidate and overwriting the newer pages.
  const requestRef = useRef(0)

  useEffect(() => {
    if (!source) {
      setPages([])
      setError(null)
      return undefined
    }

    const requestId = ++requestRef.current
    let objectUrl = null
    let pdf = null
    let cancelled = false

    const render = async () => {
      setIsRendering(true)
      setError(null)
      try {
        const pdfjsLib = await loadPdfjs()
        const url = typeof source === 'string' ? source : (objectUrl = URL.createObjectURL(source))
        pdf = await pdfjsLib.getDocument({ url }).promise

        const rendered = []
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) return
          // Sequential on purpose: pdfjs shares one worker, so rendering pages
          // in parallel queues them anyway while holding every canvas in memory.
          // eslint-disable-next-line no-await-in-loop
          const page = await pdf.getPage(pageNum)
          const viewport = page.getViewport({ scale: RENDER_SCALE })
          const canvas = document.createElement('canvas')
          canvas.width = viewport.width
          canvas.height = viewport.height
          // eslint-disable-next-line no-await-in-loop
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
          rendered.push(canvas.toDataURL())
          // Free the backing store as we go; a long resume otherwise holds
          // every full-size canvas alive at once.
          canvas.width = 0
          canvas.height = 0
        }

        if (!cancelled && requestRef.current === requestId) {
          setPages(rendered)
        }
      } catch (e) {
        console.error('Unable to render resume PDF:', e)
        if (!cancelled && requestRef.current === requestId) {
          setPages([])
          setError('render-failed')
        }
      } finally {
        if (!cancelled && requestRef.current === requestId) {
          setIsRendering(false)
        }
      }
    }

    render()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      pdf?.destroy?.()
    }
  }, [source])

  return { pages, isRendering, error }
}
